import { IconEdit } from '@consta/icons/IconEdit'
import { IconLink } from '@consta/icons/IconLink'
import { IconReply } from '@consta/icons/IconReply'
import { IconShare } from '@consta/icons/IconShare'
import { IconTrash } from '@consta/icons/IconTrash'
import { ContextMenu } from '@consta/uikit/ContextMenu'
import { Text } from '@consta/uikit/Text'
import { CreateProjectModal, PublishingModal } from '@core/components'
import {
  removeProject,
  selectPreferredName,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { SpaEventId } from '@core/core/types'
import { getProjectDataAnalyticParams } from '@core/core/utils'
import { useSpa } from '@core/hooks'
import { DeleteModal, Show } from '@library/components'
import { useBooleanFlagApi } from '@library/hooks'
import { createUrlTo } from '@library/model'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'

import css from './ProjectContextMenu.css'
import type { IContextMenuItem, IProjectContextMenuProps } from './types'

const renderRightSide = (item: IContextMenuItem): ReactNode => {
  const Icon = item.icon
  return Icon && <Icon className={css.icon} size="s" />
}

const contextMenuStyles = { width: '24rem' }

export const ProjectContextMenu: FC<IProjectContextMenuProps> = ({
  isContextMenuOpened,
  params,
  closeMenu,
  innerRef,
}) => {
  const projectId = params?.id
  const deleteModalApiRef = useBooleanFlagApi()
  const publishingModalApiRef = useBooleanFlagApi()
  const username = useAppSelector(selectPreferredName)

  const isPrivate = params?.state === 'Private'
  const isOwned = params?.createdBy === username

  const [isEditingModalOpened, setIsEditingModalOpened] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const sendSpaEvent = useSpa()

  const closeDlg = () => {
    setIsEditingModalOpened(false)
  }

  const sendSpaEvents = (eventId: SpaEventId) => {
    sendSpaEvent(eventId, getProjectDataAnalyticParams(params?.name, params?.type))
  }

  const handleUpdateClick = () => {
    setIsEditingModalOpened(true)
    sendSpaEvents('project_edit_click')
    closeMenu()
  }

  const handleDeleteClick = () => {
    if (!projectId) {
      return
    }

    dispatch(removeProject(projectId))
    closeMenu()
    sendSpaEvents('project_delete')
  }
  const handleCopyLink = () => {
    if (!projectId) {
      closeMenu()
      return
    }

    navigator.clipboard.writeText(
      createUrlTo({
        base: 'Project',
        path: projectId,
        full: true,
      }),
    )
    closeMenu()
  }

  const privateActions = isOwned
    ? [
        {
          label: 'Изменить параметры',
          icon: IconEdit,
          onClick: handleUpdateClick,
        },
        {
          label: isPrivate ? 'Опубликовать' : 'Снять с публикации',
          icon: isPrivate ? IconReply : IconShare,
          onClick: () => {
            publishingModalApiRef.current.setTrue()
            closeMenu()
          },
        },
        {
          label: 'Удалить',
          icon: IconTrash,
          onClick: () => {
            deleteModalApiRef.current.setTrue()
            closeMenu()
          },
        },
      ]
    : []

  const items: IContextMenuItem[] = [
    ...privateActions,
    {
      label: 'Скопировать ссылку',
      icon: IconLink,
      onClick: handleCopyLink,
    },
  ]

  return (
    <>
      <CreateProjectModal
        isModalOpen={isEditingModalOpened}
        closeModal={closeDlg}
        projectToUpdate={params}
      />

      <ContextMenu
        isOpen={isContextMenuOpened}
        anchorRef={innerRef}
        style={contextMenuStyles}
        size="s"
        direction="downStartLeft"
        items={items}
        getItemGroupId={item => item.group}
        getItemLabel={item => item.label}
        getItemRightSide={renderRightSide}
        getItemOnClick={item => item.onClick}
        onClickOutside={closeMenu}
      />

      <Show when={params}>
        {({ id, name, type, state }) => {
          return (
            <PublishingModal
              ref={publishingModalApiRef}
              projectId={id}
              name={name}
              projectType={type}
              state={state}
            />
          )
        }}
      </Show>

      <DeleteModal ref={deleteModalApiRef} handleDelete={handleDeleteClick}>
        <Text size="xs" view="secondary">
          Вы уверены, что хотите переместить проект в корзину?
        </Text>
      </DeleteModal>
    </>
  )
}
